/* ============================================================================
   M13 · UGC video wall behaviour.

   Every <video> starts life inside an inert <template> with no source — the
   only unconditional load is the poster <img>. This file's whole job is to
   decide, for however many clips a merchant adds, which 1-2 ever get hydrated
   into a real <video> element, and to tear those back down the moment they
   are no longer the ones on screen. That bound is what keeps the module safe
   at 6 clips today and 40 later, and it's what keeps this off the LCP path
   the client's current site is losing 22.5s of mobile LCP to autoplay video.
   ========================================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasObserver = 'IntersectionObserver' in window;
  var MAX_ACTIVE = 2;

  document.querySelectorAll('[data-ugc-wall]').forEach(init);

  function init(root) {
    var rail = root.querySelector('[data-ugc-rail]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-ugc-item]'));
    if (!rail || !items.length) return;

    var prevBtn = root.querySelector('[data-ugc-prev]');
    var nextBtn = root.querySelector('[data-ugc-next]');
    var intervalMs = (parseFloat(root.getAttribute('data-rotate-seconds')) || 5) * 1000;

    var active = []; // indices currently hydrated + playing, most-recent last

    function videoEl(item) { return item.querySelector('video'); }

    function hydrate(item) {
      var existing = videoEl(item);
      if (existing) return existing;
      var tmpl = item.querySelector('template[data-ugc-source]');
      var frame = item.querySelector('[data-ugc-frame]');
      if (!tmpl || !frame) return null;
      // Nothing to clone in (no video/video_url set on this block) — the
      // <video> shell has no <source>, and the pending flag in the markup
      // already covers this case, so there is nothing useful to hydrate.
      if (!tmpl.content.querySelector('source')) return null;
      frame.appendChild(tmpl.content.cloneNode(true));
      var video = videoEl(item);
      if (video) { video.muted = true; video.defaultMuted = true; }
      return video;
    }

    function dehydrate(item) {
      var video = videoEl(item);
      if (!video) return;
      video.pause();
      // Drop the sources before removal so nothing keeps buffering in the
      // background once the element is gone.
      Array.prototype.slice.call(video.querySelectorAll('source')).forEach(function (s) { s.remove(); });
      video.removeAttribute('src');
      try { video.load(); } catch (e) { /* no-op: element is being discarded anyway */ }
      video.remove();
      item.classList.remove('is-playing');
    }

    function play(item) {
      var video = hydrate(item);
      if (!video) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        // iOS/Safari can reject an autoplay attempt outside a user gesture;
        // the poster stays visible and the mute/play controls stay usable.
        p.catch(function () {});
      }
      item.classList.add('is-playing');
    }

    function pauseOnly(item) {
      var video = videoEl(item);
      if (video) video.pause();
      item.classList.remove('is-playing');
    }

    function setActive(nextIndexes) {
      active.filter(function (i) { return nextIndexes.indexOf(i) === -1; })
        .forEach(function (i) { dehydrate(items[i]); });
      active = nextIndexes.slice(0, MAX_ACTIVE);
      active.forEach(function (i) { play(items[i]); });
    }

    /* --- Visibility-driven playback ---------------------------------------- */

    if (!reduceMotion && hasObserver) {
      var ratios = {};
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          ratios[items.indexOf(entry.target)] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        var visible = Object.keys(ratios)
          .map(Number)
          .filter(function (i) { return ratios[i] > 0.5; })
          .sort(function (a, b) { return ratios[b] - ratios[a]; });
        setActive(visible);
      }, { root: rail, threshold: [0, 0.5, 0.75] });

      items.forEach(function (item) { observer.observe(item); });
    } else if (!reduceMotion) {
      // No IntersectionObserver: play only the first clip rather than guess.
      setActive([0]);
    }

    /* --- Reduced motion: explicit per-clip play/pause, no autoplay --------- */

    items.forEach(function (item, i) {
      var playBtn = item.querySelector('[data-ugc-play]');
      if (!playBtn) return;
      playBtn.addEventListener('click', function () {
        var isPlaying = item.classList.contains('is-playing');
        setActive(isPlaying ? [] : [i]);
        playBtn.setAttribute('aria-pressed', String(!isPlaying));
      });
    });

    /* --- Mute (exclusive — unmuting one clip mutes the rest) --------------- */

    items.forEach(function (item) {
      var muteBtn = item.querySelector('[data-ugc-mute]');
      var label = item.querySelector('[data-ugc-mute-label]');
      if (!muteBtn) return;
      muteBtn.addEventListener('click', function () {
        var video = hydrate(item);
        if (!video) return;
        var unmute = video.muted;
        items.forEach(function (other) {
          var v = videoEl(other);
          if (v && v !== video) v.muted = true;
          var b = other.querySelector('[data-ugc-mute]');
          if (b && b !== muteBtn) b.setAttribute('aria-pressed', 'false');
        });
        video.muted = !unmute;
        muteBtn.setAttribute('aria-pressed', String(unmute));
        if (label) label.textContent = (unmute ? 'Mute' : 'Unmute') + ' video';
      });
    });

    /* --- Auto-advance the rail ---------------------------------------------- */

    var timer = null;

    function stepWidth() {
      if (!items.length) return 0;
      var rect = items[0].getBoundingClientRect();
      var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || '0') || 0;
      return rect.width + gap;
    }

    function advance() {
      var step = stepWidth();
      if (!step) return;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + step, behavior: 'smooth' });
    }

    function start() {
      if (reduceMotion || timer || items.length < 2) return;
      timer = setInterval(advance, intervalMs);
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    start();
    root.addEventListener('pointerenter', stop);
    root.addEventListener('pointerleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) start();
    });

    if (prevBtn) prevBtn.addEventListener('click', function () {
      stop();
      rail.scrollBy({ left: -stepWidth(), behavior: 'smooth' });
      start();
    });
    if (nextBtn) nextBtn.addEventListener('click', function () {
      stop();
      rail.scrollBy({ left: stepWidth(), behavior: 'smooth' });
      start();
    });

    /* --- Hard-pause everything when the tab is hidden ----------------------- */

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
        items.forEach(pauseOnly);
      } else {
        start();
        active.forEach(function (i) { play(items[i]); });
      }
    });
  }
})();
